import React, { useMemo } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import ByQuestionStudentChart from 'components/ReportsModule/components/KeyboardingReport/Classroom/charts/ByQuestionStudentChart';
import { useTranslation } from 'react-i18next';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import DownloadListCard from 'components/ReportsModule/components/KeyboardingReport/AccessmentAcceleratorComponents/DownloadListCard';
import { BuildAssessmentTypes } from 'components/ReportsModule/components/KeyboardingReport/keyboarding-report-types';
import useStore from 'store';
import ConstructAResponseModal from 'components/ReportsModule/components/KeyboardingReport/AccessmentAcceleratorComponents/ConstructAResponseModal';

type Props = {
  data: BuildAssessmentTypes[];
  attempt1Completed: string;
  attempt2Completed: string;
};

const filterResponseData = (data: any[]) => {
  return data?.filter(
    x =>
      x.a1_construct_a_response || x.a2_construct_a_response || x.a1_multi_part || x.a2_multi_part
  );
};

const buildModuleLabels = (data: any[]) => {
  if (data.some(x => x.a1_question)) {
    return data?.map(x => x.a1_question);
  }
  return data?.map(x => x.a2_question);
};

const buildModuleSeries: any = (data: any[], attempt1: string, attempt2: string) => {
  const result = {
    attempt1: data?.some(x => x[attempt1]) ? data?.map(x => x[attempt1]) : undefined,
    attempt2: data?.some(x => x[attempt2]) ? data?.map(x => x[attempt2]) : undefined
  };
  return result;
};

const SingleStudentChart = ({ data, attempt1Completed, attempt2Completed }: Props) => {
  const { t } = useTranslation('Reports');
  const classes = useStyles();
  const [, { openQuarterModal }] = useStore();

  const openConstructAResponse = (data: any) => {
    openQuarterModal({
      body: <ConstructAResponseModal data={data} />,
      options: {
        paperProps: { className: classes.modalContainer }
      }
    });
  };

  const responseData = useMemo(() => filterResponseData(data), [data]);
  return (
    <>
      <Box marginBottom={4}>
        <ByQuestionStudentChart
          title={t('timePerQuestion', { ns: 'ReportsHelpCards' })}
          yAxisText={t('Time to Answer (Seconds)')}
          labels={buildModuleLabels(data)}
          {...buildModuleSeries(data, 'a1_timeToAnswer', 'a2_timeToAnswer')}
          attempt1SeriesTitle={attempt1Completed}
          attempt2SeriesTitle={attempt2Completed}
          colors={['#7FAD6A', '#404261']}
          helpText={t('timeToCompleteQuestionHelp', { ns: 'ReportsHelpCards' })}
          toolTipText={t('Seconds')}
        />
      </Box>
      <Box>
        <ByQuestionStudentChart //timeToCompleteQuestionHelp
          title={t('triesPerQuestion', { ns: 'ReportsHelpCards' })}
          yAxisText={t('triesPerQuestion', { ns: 'ReportsHelpCards' })}
          helpText={t('triesPerQuestionHelp', { ns: 'ReportsHelpCards' })}
          labels={buildModuleLabels(data)}
          attempt1SeriesTitle={attempt1Completed}
          attempt2SeriesTitle={attempt2Completed}
          colors={['#7FAD6A', '#404261']}
          toolTipText="Tries"
          {...buildModuleSeries(data, 'a1_noOfTries', 'a2_noOfTries')}
        />
      </Box>

      {responseData.length > 0 && (
        <DownloadListCard
          Icon={InsertDriveFileIcon}
          data={responseData}
          xs={responseData?.length > 2 ? 12 : 6}
          onClick={openConstructAResponse}
        />
      )}
    </>
  );
};
const useStyles = makeStyles(() => ({
  modalContainer: {
    width: 400
  }
}));

export default SingleStudentChart;
